/**
 * VAT Calculation Utilities
 */

/**
 * Calculate VAT amount from a net amount
 */
export function calculateVAT(netAmount: number, vatRate: number): number {
  if (netAmount <= 0 || vatRate < 0) return 0;
  return (netAmount * vatRate) / 100;
}

/**
 * Calculate gross amount (net + VAT)
 */
export function calculateGrossAmount(netAmount: number, vatRate: number): number {
  if (netAmount <= 0) return 0;
  return netAmount + calculateVAT(netAmount, vatRate);
}

/**
 * Calculate net amount from a gross amount
 */
export function calculateNetAmount(grossAmount: number, vatRate: number): number {
  if (grossAmount <= 0 || vatRate < 0) return 0;
  return grossAmount / (1 + vatRate / 100);
}

/**
 * Format VAT breakdown for display
 */
export function getVATBreakdown(
  amount: number,
  vatRate: number,
  isGross: boolean = false
): { net: number; vat: number; gross: number } {
  if (isGross) {
    const net = calculateNetAmount(amount, vatRate);
    const vat = amount - net;
    return { net, vat, gross: amount };
  } else {
    const vat = calculateVAT(amount, vatRate);
    const gross = amount + vat;
    return { net: amount, vat, gross };
  }
}

/**
 * Format VAT breakdown as a readable string
 */
export function formatVATBreakdown(
  amount: number,
  vatRate: number,
  isGross: boolean = false,
  formatFn: (n: number) => string = (n) => n.toFixed(2)
): string {
  const breakdown = getVATBreakdown(amount, vatRate, isGross);
  const grossStr = formatFn(breakdown.gross);
  const netStr = formatFn(breakdown.net);
  const vatStr = formatFn(breakdown.vat);
  
  return `${grossStr} (Net: ${netStr} + VAT: ${vatStr})`;
}
